import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { Col, Container, Form, Row } from "react-bootstrap";

import { DELETE_WHEEL_FORTUNE_EVENT, GET_WHEEL_FORTUNE_EVENT, UPDATE_WHEEL_FORTUNE_EVENT } from "../graphql";
import { 
  DeleteWheelFortuneEventMutation,
  DeleteWheelFortuneEventMutationVariables,
  GetWheelFortuneEventQuery,
  GetWheelFortuneEventQueryVariables,
  LevelRewardInput,
  LevelRewardInputType,
  UpdateWheelFortuneEventMutation,
  UpdateWheelFortuneEventMutationVariables,
  WheelFortuneEventConfig
} from "../../../../../../__gqltypes__";
import { LoadingLogo } from "../../../../devtools/components/Modal";
import { stringToEnum } from "../../SaleEvent/utils";
import { REQUIRED_NUMBER_OF_REWARDS, WheelFortuneRewardsData } from "./WheelFortuneRewardsData";
import { DeleteModal } from "../../../general/DeleteModal";
import { eventMatcherDefaultConfig, EventMatchers, EventMatchersConfig } from "../../../general/EventMatchers";
import { EventBaseData, EventBaseDataConfig, eventBaseDataConfigDefault } from "../../../general/EventBaseData";

type WFRewards = WheelFortuneEventConfig['rewards'];

type Props = {
  eventId: string;
  onUpserEvent: (value: WheelFortuneEventConfig) => void;
  onRemoveEvent: (eventId: string) => void;
};

function resetMinutes(date: number | string | Date): Date {
  const newDate = new Date(date);
  newDate.setMinutes(0);
  return newDate;
};

function userIdsStringToArray(songIdsString: string): string[] | undefined {
  if (typeof songIdsString === 'string' && songIdsString.trim().length > 0) {
    return songIdsString.split(',').map((id) => id.trim());
  }
  return undefined;
}

function userIdsArrayToString(userIdsArray: string[] | string): string {
  if (Array.isArray(userIdsArray)) {
    return userIdsArray.join(', ');
  }
  return userIdsArray;
}

const SPIN_PRICE_CURRENCY: Array<LevelRewardInputType> = [
  LevelRewardInputType.CURRENCY_DIAMOND,
  LevelRewardInputType.CURRENCY_COIN,
];

const rewardWithAmount = new Set<LevelRewardInputType>([
  LevelRewardInputType.CURRENCY_BOOST,
  LevelRewardInputType.CURRENCY_COIN,
  LevelRewardInputType.CURRENCY_DIAMOND,
  LevelRewardInputType.CURRENCY_FREE_SPIN,
  LevelRewardInputType.CURRENCY_SHARD_BRONZE,
  LevelRewardInputType.CURRENCY_SHARD_SILVER,
  LevelRewardInputType.CURRENCY_SHARD_GOLD,
  LevelRewardInputType.CURRENCY_META_FRAGMENT_BRONZE,
  LevelRewardInputType.CURRENCY_META_FRAGMENT_SILVER,
  LevelRewardInputType.CURRENCY_META_FRAGMENT_GOLD,
  LevelRewardInputType.CURRENCY_CATEGORY_FRAGMENT,
  LevelRewardInputType.CURRENCY_TOURNAMENT_TICKET,
]);

function amountChecker(rew: LevelRewardInput): boolean {
  return !rewardWithAmount.has(rew.type) || (Number.isInteger(rew.amount) && rew.amount > 0);
}

export function WheelFortuneEventData({eventId, onRemoveEvent, onUpserEvent}: Props): JSX.Element {

  const [baseData, setBaseData] = useState<EventBaseDataConfig>(eventBaseDataConfigDefault);
  const [filterConfig, setFilterConfig] = useState<EventMatchersConfig>(eventMatcherDefaultConfig);
  const [spinPrice, setSpinPrice] = useState<LevelRewardInput>(
    {
      amount: 25,
      type: LevelRewardInputType.CURRENCY_DIAMOND,
    });
  const [spinPricePlus, setSpinPricePlus] = useState<LevelRewardInput>(
    {
      amount: 25,
      type: LevelRewardInputType.CURRENCY_DIAMOND,
    });
  const [rewards, setRewards] = useState<WFRewards>([]);

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  
  const {data: eventData, loading: eventDataLoading, refetch} = useQuery<
    GetWheelFortuneEventQuery,
    GetWheelFortuneEventQueryVariables
  >(GET_WHEEL_FORTUNE_EVENT, {
      variables: {
        id: eventId,
      },
      fetchPolicy: 'no-cache',
    }
  );

  const [updateWF, {loading: updateLoading}] = useMutation<
    UpdateWheelFortuneEventMutation,
    UpdateWheelFortuneEventMutationVariables
  >(UPDATE_WHEEL_FORTUNE_EVENT,
    {
      onCompleted: ({updateWheelFortuneEvent}) => {
        const updated = updateWheelFortuneEvent.event;
        onUpserEvent(updated);
        refetch();
      },
      onError: (error) => {
        console.log(error);
      },
    }
  );

  const [deleteWF, { loading: deleteLoading }] = useMutation<
    DeleteWheelFortuneEventMutation,
    DeleteWheelFortuneEventMutationVariables
   >(DELETE_WHEEL_FORTUNE_EVENT,
      {
        onCompleted: ({deleteWheelFortuneEvent}) => {
          if (deleteWheelFortuneEvent) {
            onRemoveEvent(eventId);
          } else {
            alert("Error: couldn't remove the current Wheel Fortune");
          }
        },
        onError: (error) => {
          console.log(error);
        },
      }
   );

  useEffect(() => {
    if (eventData && eventData.liveOps.getWheelFortuneEvent) {
      const ed = eventData.liveOps.getWheelFortuneEvent;
      setBaseData({
        name: ed.name,
        startDate: ed.startDate,
        stopDate: ed.stopDate,
        status: ed.status,
      });
      setSpinPrice(ed.spinPrice);
      setSpinPricePlus(ed.spinPricePlus);
      setRewards(ed.rewards);

      setFilterConfig({
        filterAppVersion: ed.filterAppVersion ?? undefined,
        filterSubscriptionTier: ed.filterSubscriptionTier ?? undefined,
        filterUserIds: userIdsArrayToString(ed.filterUserIds ?? []),
        filterUserLevel: ed.filterUserLevel ?? undefined,
        filterUserMinCreationDate: ed.filterUserMinCreationDate ?? undefined,
        filterUserMaxCreationDate: ed.filterUserMaxCreationDate ?? undefined,
      });
    }
  }, [eventData]);

  const handleUpdateRewards = useCallback((rewards_: WFRewards) => {
    setRewards(rewards_);
  }, []);

  const handleSave = () => {
    updateWF({
      variables: {
        input: {
          id: eventId,
          ...baseData,
          rewards: rewards.map((val) => {
            return {
              weight: val.weight,
              reward: {
                type: val.reward.type,
                amount: val.reward.amount,
                itemId: val.reward.itemId,
              }
            };
          }),
          spinPrice: {
            type: spinPrice.type,
            amount: spinPrice.amount,
          },
          spinPricePlus: {
            type: spinPricePlus.type,
            amount: spinPricePlus.amount,
          },
          ...filterConfig,
          filterUserIds: userIdsStringToArray(filterConfig.filterUserIds ?? ''),
        },
      },
    });
  };

  const handleDelete = useCallback(() => {
    setShowDeleteModal(false);
    deleteWF({
      variables: {
        id: eventId,
      }
    });
  }, [deleteWF, eventId]);

  const handleChangePriceAmount = useCallback((event, isPlus = false) => {
    const src = parseInt(event.currentTarget.value);
    if (isPlus) {
      setSpinPricePlus(val => ({...val, amount: Number.isNaN(src) ? 0 : src}));
    } else {
      setSpinPrice(val => ({...val, amount: Number.isNaN(src) ? 0 : src}));
    }
  }, []);

  const handleChangePriceCurrency = useCallback((event, isPlus = false) => {
    if (isPlus) {
      setSpinPricePlus(val => ({...val, type: event.currentTarget.value as LevelRewardInputType}));
    } else {
      setSpinPrice(val => ({...val, type: event.currentTarget.value as LevelRewardInputType}));
    }
  }, []);

  const isAllRewardsValid = useMemo(() => {
    return rewards.every((wfRev) => amountChecker(wfRev.reward));
  }, [rewards]);

  const loading = eventDataLoading || deleteLoading || updateLoading;
  if (loading) {
    return (<LoadingLogo show />);
  }

  return (
    <>
      <Container className="pb-4">
        <EventBaseData 
          eventId={eventId}
          data={baseData}
          onUpdate={setBaseData}
          onShowDeleteModal={() => setShowDeleteModal(true)}
          onSave={handleSave}
          canSave={isAllRewardsValid && rewards.length === REQUIRED_NUMBER_OF_REWARDS}
          />
       <Row className="mb-3">
          <Col md={3} />
          <Col md={3} className="d-flex flex-column align-items-center">Basic</Col>
          <Col md={3} className="d-flex flex-column align-items-center">Plus</Col>
        </Row>
        <Row className="mb-3">
          <Col md={3}>Spin price currency:</Col>
          <Col md={3}>
            <Form.Group>
              <Form.Control as="select" type="text" value={spinPrice.type} onChange={handleChangePriceCurrency}>
                {SPIN_PRICE_CURRENCY.map((option) => (
                  <option key={option} value={stringToEnum(option, LevelRewardInputType)}>
                    {option}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Control as="select" type="text" value={spinPricePlus.type} onChange={(e) => handleChangePriceCurrency(e, true)}>
                {SPIN_PRICE_CURRENCY.map((option) => (
                  <option key={option} value={stringToEnum(option, LevelRewardInputType)}>
                    {option}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={3}>Spin price amount:</Col>
          <Col md={3}>
            <Form.Group>
              <Form.Control className="w-50" type="text" value={spinPrice.amount} onChange={handleChangePriceAmount} />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Control className="w-50" type="text" value={spinPricePlus.amount} onChange={(e) => handleChangePriceAmount(e, true)} />
            </Form.Group>
          </Col>
        </Row>

        <EventMatchers config={filterConfig} onUpdate={setFilterConfig}/>

        <Row >
          <WheelFortuneRewardsData rewards={rewards} onUpdateEvent={handleUpdateRewards}/>
        </Row>
      </Container>
      <DeleteModal
          title="Wheel Fortune Event Event"
          showModal={showDeleteModal}
          setShowModal={setShowDeleteModal}
          onDelete={handleDelete}/>
    </>
  );
}